import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IMedicalOverview, MedicalSectionKey, getMedicalOverview } from '~/apis/medicalOverview';
import { canDoAction } from '~/utils/helper/permission';
import { selectPermission, selectUser } from '~/thunks/auth/authSlice';
import { useAppSelector } from '~/utils/redux/hooks';
import { setupRouteAbsolute } from '~/utils/routes/setup';

// The app's Senior Medical History tiles, in its order; each is gated by its own action.
// A function, not a constant: utils/routes/setup imports this page, so its paths don't exist yet at import time.
const getSections = (): { key: MedicalSectionKey; path: string; action: string }[] => [
  { key: 'medications', path: setupRouteAbsolute.medications, action: 'manageMedication' },
  { key: 'allergies', path: setupRouteAbsolute.allergies, action: 'manageAllergy' },
  { key: 'healthHistory', path: setupRouteAbsolute.healthHistory, action: 'manageHealthHistory' },
  { key: 'testResults', path: setupRouteAbsolute.testResults, action: 'manageTestResult' },
  { key: 'emergencyContacts', path: setupRouteAbsolute.emergencyContacts, action: 'manageDoctorNote' },
  { key: 'doctorContacts', path: setupRouteAbsolute.doctorContacts, action: 'manageDoctorContact' },
];

const Medical = () => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const permission = useAppSelector(selectPermission);
  const [overview, setOverview] = useState<IMedicalOverview>();

  const sections = useMemo(
    () => getSections().map((section) => ({ ...section, allowed: canDoAction(user, permission, 'seniorMedical', section.action) })),
    [user, permission]
  );

  useEffect(() => {
    // sections the account can't open aren't counted either
    getMedicalOverview(sections.filter((section) => section.allowed).map((section) => section.key)).then(setOverview);
  }, [sections]);

  const total = overview ? Object.values(overview).reduce((sum, value) => sum + value, 0) : undefined;

  return (
    <div className='flex w-full flex-col gap-4'>
      <div>
        <h1 className='text-xl font-semibold text-foreground'>{t('pages.setup.medical.title')}</h1>
        <p className='text-sm text-muted-foreground'>{t('pages.setup.medical.subtitle')}</p>
      </div>

      <p className='text-sm text-muted-foreground'>
        {t('pages.setup.medical.total')}: <span className='font-semibold text-foreground'>{total ?? '...'}</span>
      </p>

      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {sections.map((section) => {
          const body = (
            <>
              <div className='flex items-start justify-between gap-3'>
                <div>
                  <h2 className='text-sm font-semibold text-foreground'>{t(`pages.setup.${section.key}.title`)}</h2>
                  <p className='mt-1 text-xs text-muted-foreground'>{t(`pages.setup.medical.cards.${section.key}`)}</p>
                </div>
                <span className='text-3xl font-semibold text-foreground' aria-label={t('pages.setup.medical.countLabel')}>
                  {section.allowed ? overview?.[section.key] ?? '-' : '-'}
                </span>
              </div>
              <span className='mt-auto text-xs font-medium text-foreground underline'>
                {section.allowed ? t('pages.setup.medical.open') : t('pages.setup.medical.noAccess')}
              </span>
            </>
          );

          const base = 'flex flex-col gap-3 rounded-lg border border-border bg-card p-4 sm:p-5';
          return section.allowed ? (
            <Link
              key={section.key}
              to={section.path}
              className={`${base} transition hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring`}
            >
              {body}
            </Link>
          ) : (
            <div key={section.key} aria-disabled='true' className={`${base} opacity-50`}>
              {body}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Medical;
